import React, { useState, useEffect } from 'react';
import { X, Clock, RefreshCw } from 'lucide-react';
import { tradingAPI } from '../../services/api';
import { TRADING_COLORS as C } from './constants';
import CoinIcon from './CoinIcon';

const PendingOrders = ({ refresh, mode }) => {
  const [futuresOrders, setFuturesOrders] = useState([]);
  const [spotOrders, setSpotOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [futuresRes, spotRes] = await Promise.all([
          tradingAPI.getPendingOrders().catch(() => ({ data: [] })),
          tradingAPI.getSpotPendingOrders().catch(() => ({ data: [] }))
        ]);
        setFuturesOrders(futuresRes.data || []);
        setSpotOrders(spotRes.data || []);
      } catch (e) {
        console.error('Failed to load pending orders:', e);
      }
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  const handleCancelFutures = async (orderId) => {
    setCancelling(orderId);
    try {
      await tradingAPI.cancelOrder(orderId);
      setFuturesOrders(futuresOrders.filter(o => o.id !== orderId));
    } catch (e) {
      console.error('Failed to cancel futures order:', e);
    }
    setCancelling(null);
  };

  const handleCancelSpot = async (orderId) => {
    setCancelling(orderId);
    try {
      await tradingAPI.cancelSpotOrder(orderId);
      setSpotOrders(spotOrders.filter(o => o.id !== orderId));
    } catch (e) {
      console.error('Failed to cancel spot order:', e);
    }
    setCancelling(null);
  };

  const orders = mode === 'spot' ? spotOrders : futuresOrders;
  const handleCancel = mode === 'spot' ? handleCancelSpot : handleCancelFutures;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <RefreshCw size={24} color={C.yellow} className="animate-spin" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <Clock size={40} color={C.textTertiary} style={{ marginBottom: 12 }} />
        <div style={{ color: C.textTertiary, fontSize: 14 }}>Нет отложенных ордеров</div>
        <div style={{ color: C.textMuted, fontSize: 12, marginTop: 6 }}>Создайте лимитный ордер во вкладке "Торговля"</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 12 }}>
      {orders.map((order) => {
        const base = order.symbol?.replace('USDT', '') || '';
        const isLong = order.side === 'long' || order.side === 'buy';
        const priceDiff = order.limit_price && order.current_price ? ((order.limit_price - order.current_price) / order.current_price * 100) : 0;

        return (
          <div key={order.id} style={{ background: C.bgSecondary, borderRadius: 12, padding: 14, marginBottom: 10, border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CoinIcon symbol={order.symbol} size={36} />
                <div>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 15 }}>{order.symbol}</div>
                  <div style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4, background: isLong ? C.longBg : C.shortBg, color: isLong ? C.long : C.short, fontSize: 10, fontWeight: 600, marginTop: 2 }}>
                    {mode === 'spot' ? (isLong ? 'ПОКУПКА' : 'ПРОДАЖА') : (isLong ? 'LONG' : 'SHORT')} • LIMIT
                  </div>
                </div>
              </div>
              <div style={{ padding: '4px 8px', borderRadius: 6, background: C.yellowSoft, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Clock size={12} color={C.yellow} />
                <span style={{ color: C.yellow, fontSize: 11, fontWeight: 600 }}>Ожидает</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: 10, background: C.bgTertiary, borderRadius: 8, marginBottom: 10 }}>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Цена исполнения</div>
                <div style={{ color: C.yellow, fontSize: 14, fontWeight: 600, fontFamily: 'monospace' }}>${order.limit_price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 7 })}</div>
              </div>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Размер</div>
                <div style={{ color: C.text, fontSize: 14, fontWeight: 600, fontFamily: 'monospace' }}>{order.quantity?.toFixed(4)} {base}</div>
              </div>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>Сумма</div>
                <div style={{ color: C.text, fontSize: 13, fontFamily: 'monospace' }}>${(order.amount || order.margin || (order.quantity * order.limit_price))?.toFixed(2)}</div>
              </div>
              <div>
                <div style={{ color: C.textTertiary, fontSize: 10 }}>От текущей цены</div>
                <div style={{ color: priceDiff < 0 ? C.long : C.short, fontSize: 13, fontWeight: 500, fontFamily: 'monospace' }}>{priceDiff > 0 ? '+' : ''}{priceDiff.toFixed(2)}%</div>
              </div>
            </div>

            {(order.stop_loss || order.take_profit) && (
              <div style={{ display: 'flex', gap: 8, padding: '8px 10px', background: C.bgElevated, borderRadius: 6, marginBottom: 10 }}>
                {order.stop_loss && <div style={{ flex: 1 }}><div style={{ color: C.textTertiary, fontSize: 9 }}>Stop Loss</div><div style={{ color: C.short, fontSize: 12, fontWeight: 600 }}>${order.stop_loss.toFixed(2)}</div></div>}
                {order.take_profit && <div style={{ flex: 1 }}><div style={{ color: C.textTertiary, fontSize: 9 }}>Take Profit</div><div style={{ color: C.long, fontSize: 12, fontWeight: 600 }}>${order.take_profit.toFixed(2)}</div></div>}
              </div>
            )}

            <button onClick={() => handleCancel(order.id)} disabled={cancelling === order.id} style={{ width: '100%', padding: 12, borderRadius: 8, background: C.shortBg, color: C.short, border: `1px solid rgba(239, 68, 68, 0.2)`, fontSize: 13, fontWeight: 600, opacity: cancelling === order.id ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <X size={16} />{cancelling === order.id ? 'Отмена...' : 'Отменить ордер'}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default PendingOrders;
